import { useCallback, useEffect, useMemo, useState } from "react";
import { axiosInstance, buildLegacyHeaders } from "../Components/axiosFetchers.js";

export default function useVersionHistory({
  user,
  enabled = true,
  historyPath,
  currentData,
  normalizeDetail = (detail) => detail,
  onRestore = null,
  onRestoreSuccess = null,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [summaries, setSummaries] = useState([]);
  const [summaryState, setSummaryState] = useState("idle");
  const [detailState, setDetailState] = useState("idle");
  const [restoreState, setRestoreState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [activeVersionNo, setActiveVersionNo] = useState(null);
  const [detailCache, setDetailCache] = useState({});

  const loadSummaries = useCallback(async () => {
    if (!enabled || !historyPath) {
      return [];
    }

    setSummaryState("loading");
    setErrorMessage("");

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}${historyPath}/history`,
        {
          headers: buildLegacyHeaders(user),
        }
      );
      const nextSummaries = Array.isArray(response.data) ? response.data : [];

      setSummaries(nextSummaries);
      setSummaryState("loaded");

      return nextSummaries;
    } catch (error) {
      console.error(error);
      setSummaryState("error");
      setErrorMessage("We couldn't load the version history right now.");
      return [];
    }
  }, [enabled, historyPath, user]);

  const loadDetail = useCallback(
    async (versionNo) => {
      if (!enabled || !historyPath || !versionNo) {
        return null;
      }

      if (detailCache[versionNo]) {
        return detailCache[versionNo];
      }

      setDetailState("loading");
      setErrorMessage("");

      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}${historyPath}/history/${versionNo}`,
          {
            headers: buildLegacyHeaders(user),
          }
        );
        const normalizedDetail = normalizeDetail(response.data);

        setDetailCache((prevState) => ({
          ...prevState,
          [versionNo]: normalizedDetail,
        }));
        setDetailState("loaded");

        return normalizedDetail;
      } catch (error) {
        console.error(error);
        setDetailState("error");
        setErrorMessage("We couldn't load that saved version.");
        return null;
      }
    },
    [detailCache, enabled, historyPath, normalizeDetail, user]
  );

  useEffect(() => {
    setDialogOpen(false);
    setSummaries([]);
    setSummaryState("idle");
    setDetailState("idle");
    setRestoreState("idle");
    setErrorMessage("");
    setActiveVersionNo(null);
    setDetailCache({});
  }, [historyPath]);

  useEffect(() => {
    if (
      !enabled ||
      !dialogOpen ||
      summaryState !== "idle" ||
      summaries.length > 0
    ) {
      return;
    }

    loadSummaries();
  }, [dialogOpen, enabled, loadSummaries, summaries.length, summaryState]);

  const currentSummary = useMemo(
    () => summaries.find((summary) => summary?.is_current) || null,
    [summaries]
  );

  useEffect(() => {
    if (!enabled || !currentSummary || activeVersionNo !== null) {
      return;
    }

    setActiveVersionNo(currentSummary.version_no);
  }, [activeVersionNo, currentSummary, enabled]);

  const activeSummary = useMemo(() => {
    if (activeVersionNo === null) {
      return currentSummary;
    }

    return (
      summaries.find((summary) => String(summary.version_no) === String(activeVersionNo)) ||
      currentSummary
    );
  }, [activeVersionNo, currentSummary, summaries]);

  const isViewingHistoricalVersion = Boolean(activeSummary && !activeSummary.is_current);
  const activeDetail =
    activeSummary && !activeSummary.is_current
      ? detailCache[activeSummary.version_no] || null
      : currentData;

  async function openDialog() {
    setDialogOpen(true);

    if (summaryState === "idle" && summaries.length === 0) {
      const nextSummaries = await loadSummaries();
      const nextCurrentSummary = nextSummaries.find((summary) => summary?.is_current);

      if (activeVersionNo === null && nextCurrentSummary) {
        setActiveVersionNo(nextCurrentSummary.version_no);
      }
    }
  }

  function closeDialog() {
    setDialogOpen(false);
  }

  async function selectVersion(versionNo) {
    setActiveVersionNo(versionNo);

    const selectedSummary =
      summaries.find((summary) => String(summary.version_no) === String(versionNo)) || null;

    if (selectedSummary && !selectedSummary.is_current) {
      await loadDetail(selectedSummary.version_no);
    } else {
      setDetailState("idle");
    }
  }

  async function showCurrentVersion() {
    if (!currentSummary) {
      return;
    }

    setActiveVersionNo(currentSummary.version_no);
    setDetailState("idle");
  }

  const activeIndex = useMemo(() => {
    if (activeSummary == null) {
      return -1;
    }

    return summaries.findIndex(
      (summary) => String(summary.version_no) === String(activeSummary.version_no)
    );
  }, [activeSummary, summaries]);

  async function selectAdjacentVersion(direction) {
    if (activeIndex < 0) {
      return;
    }

    const nextIndex = activeIndex + direction;
    const nextSummary = summaries[nextIndex];

    if (!nextSummary) {
      return;
    }

    await selectVersion(nextSummary.version_no);
  }

  async function restoreSelectedVersion() {
    if (!onRestore || !activeSummary || activeSummary.is_current || !activeDetail) {
      return null;
    }

    setRestoreState("saving");
    setErrorMessage("");

    try {
      const restoredEntity = await onRestore(activeDetail, activeSummary);

      if (onRestoreSuccess) {
        onRestoreSuccess(restoredEntity, activeSummary);
      }

      const nextSummaries = await loadSummaries();
      const nextCurrentSummary = nextSummaries.find((summary) => summary?.is_current);

      if (nextCurrentSummary) {
        setActiveVersionNo(nextCurrentSummary.version_no);
      }

      setRestoreState("saved");
      return restoredEntity;
    } catch (error) {
      console.error(error);
      setRestoreState("error");
      setErrorMessage("We couldn't restore that version right now.");
      return null;
    }
  }

  return {
    activeSummary,
    closeDialog,
    detailState,
    dialogOpen,
    errorMessage,
    isEnabled: enabled,
    isViewingHistoricalVersion,
    openDialog,
    restoreSelectedVersion,
    restoreState,
    selectAdjacentVersion,
    selectVersion,
    showCurrentVersion,
    summaryState,
    summaries,
    versionData: activeDetail,
    canViewNewer: activeIndex > 0,
    canViewOlder: activeIndex >= 0 && activeIndex < summaries.length - 1,
  };
}
